import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, LinearProgress,
  Divider, Paper, Avatar
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Groups as TeamIcon,
  ArrowBack as ArrowBackIcon, Email as EmailIcon,
  Folder as ProjectIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { projectApi } from '../services/projectApi';
import { PrjMember, PrjProject } from '../types/project';

const emptyMember = (): Partial<PrjMember> => ({
  projectId: undefined,
  name: '',
  role: '',
  email: '',
  allocation: 100,
});

export const TeamPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [membersList, setMembersList] = useState<PrjMember[]>([]);
  const [projectsList, setProjectsList] = useState<PrjProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<PrjMember>>(emptyMember());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [members, projects] = await Promise.all([
        projectApi.getMembers(),
        projectApi.getProjects()
      ]);
      setMembersList(members);
      setProjectsList(projects);
    } catch (err) {
      console.error('Error loading team members:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyMember());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (member: PrjMember) => {
    setFormData({ ...member });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (member: PrjMember) => {
    const memberId = (member.id || member.PMB_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('project.team.confirmDelete', 'Êtes-vous sûr de vouloir retirer ce membre du projet ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await projectApi.deleteMember(memberId);
      if (res.success) {
        blobticMessage.success(t('project.team.deleteSuccess', 'Membre retiré avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.name?.trim()) {
      return t('project.team.nameRequired', 'Le nom du membre est requis.');
    }
    if (!formData.projectId && !formData.PRJ_IDF) {
      return t('project.team.projectRequired', 'Veuillez sélectionner un projet associé.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      PMB_IDF: formData.id || formData.PMB_IDF,
      PMB_CODE: formData.code || formData.PMB_CODE,
      PRJ_IDF: formData.projectId || formData.PRJ_IDF,
      PMB_NAME: formData.name,
      PMB_ROLE: formData.role,
      PMB_EMAIL: formData.email,
      PMB_ALLOCATION: Number(formData.allocation || 100),
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('project.team.editTitle', 'Modifier le Membre') : t('project.team.newTitle', 'Nouveau Membre')}
        icon={<TeamIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_ProjectManagement,prj_member', primaryKey: 'PMB_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
        onClear={() => setFormData(emptyMember())}
      >
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('project.team.details', 'Détails du membre de l\'équipe')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('project.team.project', 'Projet affecté *')}
                value={formData.projectId || formData.PRJ_IDF || ''}
                onChange={(e) => setFormData({ ...formData, projectId: Number(e.target.value), PRJ_IDF: Number(e.target.value) })}
                required
              >
                {projectsList.map((p) => (
                  <MenuItem key={p.id || p.PRJ_IDF} value={p.id || p.PRJ_IDF}>
                    {p.title} ({p.code || `PRJ-${p.id}`})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('project.team.name', 'Nom et prénom *')}
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('project.team.role', 'Rôle / Fonction')}
                value={formData.role || ''}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="email"
                label={t('project.team.email', 'Adresse Email')}
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={t('project.team.allocation', 'Taux d\'allocation (%)')}
                value={formData.allocation ?? 100}
                onChange={(e) => setFormData({ ...formData, allocation: Math.min(100, Math.max(1, parseInt(e.target.value) || 0)) })}
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
          </Grid>
        </Paper>
      </GenericPage>
    );
  }

  // Columns for GenericTable
  const columns = [
    {
      id: 'name',
      label: t('project.team.name', 'Membre'),
      minWidth: 200,
      format: (_: any, row: PrjMember) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.9rem', fontWeight: 700 }}>
            {row.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {row.name}
            </Typography>
            {row.email && (
              <Typography variant="caption" color="text.secondary">
                {row.email}
              </Typography>
            )}
          </Box>
        </Stack>
      )
    },
    {
      id: 'role',
      label: t('project.team.role', 'Rôle'),
      minWidth: 150,
      format: (_: any, row: PrjMember) => (
        <Chip
          label={row.role || '-'}
          size="small"
          variant="outlined"
          color="primary"
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      id: 'project',
      label: t('project.team.project', 'Projet affecté'),
      minWidth: 180,
      format: (_: any, row: PrjMember) => (
        <Typography variant="body2">
          {row.projectTitle || `Projet #${row.projectId}`}
        </Typography>
      )
    },
    {
      id: 'allocation',
      label: t('project.team.allocation', 'Allocation'),
      minWidth: 140,
      format: (_: any, row: PrjMember) => (
        <Box sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" fontWeight={600}>{row.allocation || 100}%</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={row.allocation || 100}
            color={row.allocation > 80 ? 'primary' : 'secondary'}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )
    }
  ];

  return (
    <GenericPage
      title={t('project.team.pageTitle', 'Équipe & Ressources Projet')}
      icon={<TeamIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
      newButtonLabel={t('project.team.new', 'Nouveau Membre')}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {membersList.map((m) => (
            <Grid item xs={12} sm={6} md={4} key={m.id || m.PMB_IDF}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50, fontWeight: 700, fontSize: '1.2rem' }}>
                      {m.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={700} noWrap>
                        {m.name}
                      </Typography>
                      {m.role && (
                        <Chip
                          label={m.role}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  </Stack>

                  {m.projectTitle && (
                    <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                      <ProjectIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {m.projectTitle}
                      </Typography>
                    </Stack>
                  )}

                  {m.email && (
                    <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                      <EmailIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {m.email}
                      </Typography>
                    </Stack>
                  )}

                  <Box sx={{ my: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">{t('project.team.allocation', 'Allocation')}</Typography>
                      <Typography variant="caption" fontWeight={700}>{m.allocation || 100}%</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={m.allocation || 100}
                      color={m.allocation > 80 ? 'primary' : 'secondary'}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Tooltip title={t('common.edit', 'Modifier')}>
                      <IconButton size="small" color="primary" onClick={() => openEdit(m)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete', 'Supprimer')}>
                      <IconButton size="small" color="error" onClick={() => handleDelete(m)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {membersList.length === 0 && !loading && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="text.secondary">
                  {t('project.team.empty', 'Aucun membre d\'équipe enregistré. Cliquez sur "Nouveau Membre" pour affecter une ressource.')}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <GenericTable
          columns={columns}
          data={membersList}
          filterColumns={['name', 'role', 'projectTitle', 'email']}
          searchPlaceholder={t('project.team.searchPlaceholder', 'Rechercher un membre par nom, rôle, projet...')}
          maxHeight="calc(100vh - 280px)"
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      )}
    </GenericPage>
  );
};

export default TeamPage;
